import * as React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { cn } from '@/lib/utils';
import { Clock, X } from 'lucide-react';
import {
	inputBaseClass,
	inputFocusRingClass,
	inputInvalidClass
} from './input';

interface TimeInputProps {
	/** Value in "HH:mm" 24-hour format (e.g. "08:30", "14:00") */
	value?: string;
	/** Called with value in "HH:mm" 24-hour format, or empty string when cleared */
	onChange?: (value: string) => void;
	/** Called on blur */
	onBlur?: () => void;
	/** HTML id attribute */
	id?: string;
	/** Whether the input is disabled */
	disabled?: boolean;
	/** Additional class names for the wrapper */
	className?: string;
}

/** Parse "HH:mm" string to a Date object (today) */
const parseTimeString = (time: string | undefined): Date | null => {
	if (!time || !time.includes(':')) return null;
	const [h, m] = time.split(':').map(Number);
	if (isNaN(h) || isNaN(m)) return null;
	const d = new Date();
	d.setHours(h, m, 0, 0);
	return d;
};

/** Format a Date to "HH:mm" */
const formatTime = (date: Date): string => {
	const h = date.getHours().toString().padStart(2, '0');
	const m = date.getMinutes().toString().padStart(2, '0');
	return `${h}:${m}`;
};

/**
 * Custom input that renders as a styled trigger matching the design system.
 */
const CustomTimeInput = React.forwardRef<
	HTMLDivElement,
	{
		value?: string;
		onClick?: () => void;
		onClear?: () => void;
		disabled?: boolean;
		className?: string;
		id?: string;
	}
>(({ value, onClick, onClear, disabled, className, id }, ref) => (
	<div
		ref={ref}
		id={id}
		role="button"
		tabIndex={disabled ? -1 : 0}
		onClick={onClick}
		onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onClick?.();
			}
		}}
		className={cn(
			inputBaseClass,
			inputFocusRingClass,
			inputInvalidClass,
			'flex items-center justify-between cursor-pointer select-none',
			disabled && 'pointer-events-none cursor-not-allowed opacity-50',
			!value && 'font-light text-placeholder',
			className
		)}
	>
		<span>{value || '--:--'}</span>
		<div className="flex items-center gap-1">
			{value && !disabled && (
				<span
					role="button"
					tabIndex={0}
					onClick={(e) => {
						e.stopPropagation();
						onClear?.();
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.stopPropagation();
							e.preventDefault();
							onClear?.();
						}
					}}
					className="rounded-full p-0.5 hover:bg-muted transition-colors"
					aria-label="Clear time"
				>
					<X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
				</span>
			)}
			<Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
		</div>
	</div>
));
CustomTimeInput.displayName = 'CustomTimeInput';

/**
 * A 24-hour time input component powered by react-datepicker in time-only mode.
 * Shows a scrollable time list dropdown. Always uses HH:mm (24h) format.
 */
const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
	({ value, onChange, onBlur, id, disabled = false, className }, ref) => {
		const selected = React.useMemo(
			() => parseTimeString(value),
			[value]
		);

		const handleChange = React.useCallback(
			(date: Date | null) => {
				if (date) {
					onChange?.(formatTime(date));
				} else {
					onChange?.('');
				}
			},
			[onChange]
		);

		return (
			<div ref={ref} className={cn('vectro-time-picker', className)}>
				<DatePicker
					selected={selected}
					onChange={handleChange}
					onCalendarClose={onBlur}
					showTimeSelect
					showTimeSelectOnly
					timeIntervals={5}
					timeFormat="HH:mm"
					dateFormat="HH:mm"
					timeCaption="Time"
					disabled={disabled}
					customInput={
						<CustomTimeInput
							id={id}
							disabled={disabled}
							onClear={() => onChange?.('')}
						/>
					}
				/>
			</div>
		);
	}
);

TimeInput.displayName = 'TimeInput';

export { TimeInput };
export type { TimeInputProps };
